import React, { useState } from 'react';
import { parse, isValid, format } from 'date-fns';

interface FloatingInputProps {
    label: string;
    name: string;
    value: string;
    onChange: (name: string, value: string | boolean) => void;
    readOnly?: boolean;
    type?: string;
    error?: string;
    placeholder?: string;
    required?: boolean;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
    helperText?: string;
    size?: 'small' | 'medium' | 'large';
    variant?: 'default' | 'filled' | 'outlined';
}

export default function FloatingInput({
    label,
    name,
    value,
    onChange,
    readOnly = false,
    type = 'text',
    error,
    placeholder,
    required = false,
    leftIcon,
    rightIcon,
    helperText,
    size = 'medium',
    variant = 'default'
}: FloatingInputProps) {
    const [isFocused, setIsFocused] = useState(false);
    const isDateField = type === 'date';
    const hasValue = value && value.length > 0;
    const shouldFloat = isFocused || hasValue;

    const getSafeDateValue = (val: string) => {
        if (!val) return '';
        try {
            const parsed = parse(val, 'dd/MM/yyyy', new Date());
            if (isValid(parsed)) return format(parsed, 'yyyy-MM-dd');
        } catch { }
        try {
            const iso = new Date(val);
            if (!isNaN(iso.getTime())) return iso.toISOString().split('T')[0];
        } catch { }
        return '';
    };

    const displayValue = isDateField ? getSafeDateValue(value) : value || '';

    const sizes = {
        small: 'px-3 pt-6 pb-2 text-sm',
        medium: 'px-4 pt-7 pb-3 text-sm',
        large: 'px-4 pt-8 pb-4 text-base',
    };

    const labelSizes = {
        small: { floating: 'text-xs top-2 left-3', normal: 'text-sm top-3 left-3' },
        medium: { floating: 'text-xs top-2 left-4', normal: 'text-sm top-4 left-4' },
        large: { floating: 'text-xs top-2 left-4', normal: 'text-base top-5 left-4' },
    };

    const variants = {
        default: `border bg-[var(--bg-surface)] ${error
            ? 'border-red-500 focus:border-red-500 focus:ring-red-500/25'
            : isFocused
                ? 'border-[var(--primary-500)] focus:border-[var(--primary-500)] focus:ring-[var(--primary-500)]/25'
                : 'border-[var(--border-subtle)] hover:border-[var(--border-glass-strong)]'
            }`,
        filled: `border-0 bg-[var(--bg-hover)] ${error
            ? 'bg-red-950/20 focus:bg-red-950/20 focus:ring-red-500/25'
            : isFocused
                ? 'bg-[var(--bg-card)] focus:bg-[var(--bg-card)] focus:ring-[var(--primary-500)]/25'
                : 'hover:bg-[var(--bg-card)]'
            }`,
        outlined: `border-2 bg-transparent ${error
            ? 'border-red-500 focus:border-red-500 focus:ring-red-500/25'
            : isFocused
                ? 'border-[var(--primary-500)] focus:border-[var(--primary-500)] focus:ring-[var(--primary-500)]/25'
                : 'border-[var(--border-subtle)] hover:border-[var(--border-glass-strong)]'
            }`,
    };

    const iconSize = size === 'small' ? 'w-4 h-4' : size === 'large' ? 'w-5 h-5' : 'w-4 h-4';
    const iconPadding = leftIcon ? (size === 'large' ? 'pl-11' : 'pl-10') : '';
    const rightIconPadding = rightIcon ? (size === 'large' ? 'pr-11' : 'pr-10') : '';

    return (
        <div className="relative w-full">
            <div className="relative">
                {leftIcon && (
                    <div className={`absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] z-10 ${iconSize}`}>
                        {leftIcon}
                    </div>
                )}

                <input
                    id={name}
                    type={type}
                    name={name}
                    value={displayValue}
                    placeholder={shouldFloat ? placeholder : ''}
                    onChange={(e) => onChange(name, e.target.value)}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    readOnly={readOnly}
                    className={`
            w-full rounded-[var(--radius-md)] transition-all duration-[var(--duration-normal)] ease-[var(--ease-calm)]
            focus:outline-none focus:ring-[3px] text-[var(--text-primary)]
            ${readOnly ? 'bg-[var(--bg-hover)] text-[var(--text-secondary)] cursor-not-allowed' : ''}
            ${sizes[size]} ${variants[variant]} ${iconPadding} ${rightIconPadding}
            ${error ? 'text-[var(--danger)]' : ''}
          `}
                />

                <label
                    htmlFor={name}
                    className={`
            absolute pointer-events-none transition-all duration-200 ease-in-out
            ${shouldFloat
                            ? `${labelSizes[size].floating} font-semibold ${error ? 'text-red-500' : isFocused ? 'text-[var(--primary-500)]' : 'text-[var(--text-secondary)]'}`
                            : `${labelSizes[size].normal} ${error ? 'text-red-500' : 'text-[var(--text-muted)]'}`
                        }
            ${variant === 'filled' && shouldFloat ? 'bg-transparent' : shouldFloat ? 'bg-[var(--bg-surface)] px-1' : ''}
          `}
                >
                    {label} {required && <span className="text-red-500">*</span>}
                </label>

                {rightIcon && (
                    <div className={`absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] ${iconSize}`}>
                        {rightIcon}
                    </div>
                )}
            </div>

            <div className="mt-1">
                {error ? (
                    <p className="text-sm text-red-500 font-medium flex items-center gap-1">
                        <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {error}
                    </p>
                ) : helperText ? (
                    <p className="text-sm text-[var(--text-secondary)]">{helperText}</p>
                ) : null}
            </div>
        </div>
    );
}
